import { useId, useRef, useState } from 'react';
import { Send, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, IconButton, Input, cn, focusRing } from '@/ui';

/** The assistant's mark: three teal orbits around a teal nucleus, one navy electron circling. */
export function AtomIcon({ size = 20, className }: { size?: number; className?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden focusable="false" className={cn('shrink-0', className)}>
      <g fill="none" stroke="var(--color-brand)" strokeWidth="1.3">
        <ellipse cx="12" cy="12" rx="10" ry="4" />
        <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(60 12 12)" />
        <ellipse cx="12" cy="12" rx="10" ry="4" transform="rotate(120 12 12)" />
      </g>
      <circle cx="12" cy="12" r="2.2" fill="var(--color-brand)" />
      <g className="origin-center animate-orbit-slow motion-reduce:animate-none" style={{ transformBox: 'view-box' }}>
        <circle cx="12" cy="3.2" r="1.6" fill="var(--color-navy)" />
      </g>
    </svg>
  );
}

/**
 * Oppenheimer, the Manhattan assistant: a floating button opening a small panel. A placeholder
 * for now ("Coming soon"): it makes no requests.
 */
export function Oppenheimer() {
  const { t } = useTranslation('shell');
  const { t: common } = useTranslation();
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const button = useRef<HTMLButtonElement>(null);
  const close = () => {
    setOpen(false);
    button.current?.focus();
  };
  return (
    <div className="pointer-events-none fixed bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-3 z-40 flex flex-col items-end gap-2 sm:bottom-4 sm:right-4">
      {open && (
        <section
          id={panelId}
          aria-label="Oppenheimer"
          onKeyDown={(e) => e.key === 'Escape' && close()}
          className="pointer-events-auto flex w-[340px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-xl border border-line bg-surface shadow-pop"
        >
          <header className="flex items-start gap-2.5 border-b border-line px-4 py-3">
            <AtomIcon size={28} />
            <div className="min-w-0 flex-1">
              <h2 className="m-0 font-display text-base font-semibold text-heading">Oppenheimer</h2>
              <p className="m-0 text-xs text-ink-muted">{t('oppenheimer.subtitle')}</p>
            </div>
            <IconButton label={common('actions.close')} icon={<X />} size="sm" onClick={close} autoFocus />
          </header>
          <div className="flex flex-col items-start gap-2 px-4 py-4">
            <Badge tone="brand">{t('oppenheimer.comingSoon')}</Badge>
            <p className="m-0 text-sm text-ink">{t('oppenheimer.pitch')}</p>
          </div>
          <footer className="flex items-center gap-2 border-t border-line bg-subtle px-3 py-2.5">
            <Input aria-label={t('oppenheimer.message')} placeholder={t('oppenheimer.placeholder')} disabled boxClassName="flex-1" />
            <IconButton label={t('oppenheimer.send')} icon={<Send />} variant="primary" disabled />
          </footer>
        </section>
      )}
      <button
        ref={button}
        type="button"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          // phones: a round icon button that covers as little as possible; the name stays for screen readers
          'pointer-events-auto m-0 inline-flex size-11 cursor-pointer items-center justify-center gap-2 rounded-full border border-line bg-surface p-0 font-sans text-sm font-bold text-ink shadow-sm hover:border-line-strong hover:bg-subtle',
          'sm:h-10 sm:w-auto sm:justify-start sm:pl-3 sm:pr-4',
          focusRing,
        )}
      >
        <AtomIcon />
        <span className="sr-only sm:not-sr-only">{t('oppenheimer.open')}</span>
      </button>
    </div>
  );
}
